import { useEffect } from "react";
import confetti from "canvas-confetti";

/**
 * Konfetti-Moment bei gewonnenem Angebot (`aufgaben.md` "Konfetti-Moment bei gewonnenem
 * Angebot", Antoine 23.08.2026).
 *
 * ── Migration 352: der Trigger ist jetzt `justCelebrated`, nicht mehr `status` ────────────────
 * Bis 02.09.2026 verglich diese Komponente `EventStatus` gegen `WON_EVENT_STATUSES =
 * {"offer_chosen", "paid"}` selbst (per `useRef`, um nur beim ECHTEN Uebergang zu feuern, nie
 * beim Neuladen einer bereits gewonnenen Seite). Das hatte zwei Schwaechen (Architektur-Plan
 * "gewonnen" (OR-Signal) + einmalige Konfetti-Feier, § 4d, Recherche-Befund 2c):
 *  1. Es kannte nur zwei von vier moeglichen "gewonnen"-Signalen (Angebot angenommen ODER
 *     Zahlung gedeckt) — eine signierte Kostenuebernahme OHNE vorherige oeffentliche
 *     Angebots-Annahme setzte zwar `offer_chosen` (Konfetti feuerte), zaehlte aber nirgends
 *     als CR-Auftrag — zwei verschiedene Antworten auf dieselbe Frage.
 *  2. Die Einmaligkeit war rein CLIENT-seitig und NICHT persistent: ein `useRef` merkt sich
 *     nur den vorherigen Status im React-Speicher. Wechselte der Status, waehrend die
 *     Detailseite NICHT offen war, feuerte die Animation nie — weder beim naechsten Oeffnen
 *     noch sonst (siehe der alte Test "kein Blitz beim Neuladen").
 *
 * Seit Migration 352 beantwortet der SERVER beide Fragen mit EINER Funktion (`leiteGewinnAb`,
 * `apps/api/src/lib/gewinn.ts` — ODER aus vier Signalen: Zahlung gedeckt, manueller Schalter,
 * Kostenuebernahme signiert, Angebot angenommen) und garantiert die Einmaligkeit selbst,
 * PERSISTENT: `GET /api/events/:id` claimt `events.win_celebrated_at` atomar
 * (`UPDATE ... WHERE win_celebrated_at IS NULL RETURNING id`) und liefert `justCelebrated: true`
 * GENAU EINMAL, fuer immer, egal ob die Seite beim Uebergang offen war. Der Client braucht seine
 * bisherige Heuristik deshalb nicht mehr: er feuert einfach, sobald `justCelebrated === true`
 * ankommt — der Server hat den "nur einmal"-Teil schon erledigt.
 */

/**
 * Ob eine reduzierte-Bewegung-Praeferenz eingestellt ist — dieselbe Abfrage wie
 * `ui/theme.ts` fuer `prefers-color-scheme`, hier fuer `prefers-reduced-motion`.
 * `canvas-confetti` kennt diese Praeferenz nicht selbst (kein eingebauter Support), darum
 * der Guard hier statt in der Bibliothek.
 */
function bewegungReduziert(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Ein kurzer, einmaliger Burst (canvas-confetti erzeugt sein eigenes `<canvas>`-Overlay
 * und raeumt es nach dem Ausklingen selbst wieder ab — kein Element vorzuhalten). Keine
 * Endlosschleife, kein Sound (siehe Aufgabenbeschreibung "explizit nicht im Scope").
 */
export function feuereKonfetti(): void {
  if (bewegungReduziert()) return;
  void confetti({
    particleCount: 140,
    spread: 75,
    startVelocity: 45,
    origin: { y: 0.65 },
    ticks: 200,
    // Eigener Guard oben reicht schon (kein Aufruf ueberhaupt) — dieses Flag ist ein zweites,
    // von canvas-confetti selbst dokumentiertes Netz (README "Reduced Motion"), falls die
    // Funktion je ohne den Guard aufgerufen wuerde.
    disableForReducedMotion: true,
  });
}

/**
 * Unsichtbare Wach-Komponente: rendert nichts, beobachtet nur `justCelebrated`.
 *
 * Kein `useRef`/Statusvergleich mehr noetig (s. Kopfkommentar): der Server garantiert bereits
 * "genau einmal, fuer immer" ueber `events.win_celebrated_at`. `justCelebrated=true` bedeutet
 * damit ausnahmslos "DIESER Abruf hat gerade als allerester ueberhaupt geclaimt" — der Effect
 * feuert deshalb bei jedem Wechsel auf `true`, auch beim allerersten Mount, und dank der
 * `[justCelebrated]`-Abhaengigkeit kein zweites Mal, solange der Wert `true` bleibt.
 */
export function GewonnenKonfetti({ justCelebrated }: { justCelebrated: boolean }) {
  useEffect(() => {
    if (justCelebrated) feuereKonfetti();
  }, [justCelebrated]);
  return null;
}
